/**
 * Feuille qui monte du bas.
 *
 * C'est le contenant du mobile : elle apparaît là où le pouce est déjà, elle n'efface pas
 * l'écran qu'on regardait, et elle se referme d'un geste vers le bas — trois choses qu'une
 * page de plus ne sait pas faire.
 *
 * Deux emplois dans l'application, et ils ont dicté sa forme : les entrées de navigation
 * qui ne tiennent pas dans la barre, et la saisie rapide. Dans les deux cas le contenu
 * est une liste de cibles qu'on touche sans regarder, d'où la hauteur libre et le
 * défilement interne plutôt qu'un panneau qui s'ajuste au contenu.
 *
 * **Quatre portes de sortie**, parce qu'une feuille dont on ne sait pas sortir est un
 * piège : le geste vers le bas, l'appui hors de la feuille, la touche Échap, et un bouton
 * nommé. Le geste est un raccourci — jamais la seule porte, c'est la règle du projet.
 */

import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { cx } from '@/lib/cx';
import { useDownwardSwipe } from '@/lib/swipe';

import styles from './Sheet.module.css';

export function Sheet({
  open,
  onClose,
  title,
  /** Décrit ce que la feuille sert à faire, sous le titre. */
  lede,
  fit,
  wide,
  action,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  lede?: string | undefined;
  /**
   * **Une action d'en-tête**, à droite du titre (`NUT-28`).
   *
   * Née pour « Scanner » dans « Noter » : « tout en haut », c'est l'en-tête — le corps
   * défile, et une première rangée y serait déjà sous ce qui précède. Une seule action :
   * deux pictogrammes à côté d'un titre font une barre d'outils, et une feuille n'en a pas.
   *
   * **Le pictogramme et le mot**, jamais le pictogramme seul : la barre d'onglets ne laisse
   * aucune icône sans son mot, et un dessin de code-barres ne dit pas ce qu'il fait. Le nom
   * accessible peut dire plus long que le mot (`label`).
   *
   * Dessinée ici et dans `Sheet.module.css` : le style suit le composant, et aucun écran
   * ne peint son propre bouton d'en-tête.
   */
  action?:
    | {
        /** Le mot visible. */
        text: string;
        /** Le nom accessible, quand le mot seul ne dit pas tout. */
        label?: string | undefined;
        icon: ReactNode;
        onClick: () => void;
      }
    | undefined;
  /**
   * La feuille suit la hauteur de son contenu, au lieu de tenir la hauteur commune.
   *
   * Le défaut est l'inverse — voir `.panel` : un panneau qui s'ajuste à ce qu'il porte
   * saute sous le pouce dès que son contenu change, et « Ajouter un repas » en donnait
   * cinq hauteurs. Ce drapeau est la sortie nommée, pour les surfaces **dont le contenu
   * ne change pas** et qui sont assez courtes pour que le vide se remarque plus que le
   * saut : aujourd'hui le seul choix du mode de saisie d'un repas.
   *
   * Il ne s'emploie donc pas pour « faire plus joli à l'ouverture » d'une surface qui va
   * grandir — ce serait rendre le saut à la feuille qui vient de s'en débarrasser.
   */
  fit?: boolean | undefined;
  /**
   * **Deux vues, petit et grand écran.** Sur téléphone, rien ne change : la feuille monte
   * du bas. Sur un écran large, elle s'élargit pour qu'un formulaire riche — une séance à
   * écrire et son aperçu — se lise sur deux colonnes au lieu d'une bande de 480 px.
   */
  wide?: boolean | undefined;
  children: ReactNode;
  className?: string | undefined;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Ce qui avait le focus avant l'ouverture. Le rendre est ce qui distingue une feuille
  // d'une page : on revient exactement où on était.
  const restore = useRef<HTMLElement | null>(null);

  const swipe = useDownwardSwipe({ track: true, onDown: onClose });

  /**
   * La fermeture, **hors des dépendances de l'effet**.
   *
   * L'effet en dépendait, et `onClose` est presque toujours une lambda : son identité
   * change à chaque rendu de l'appelant. L'effet se rejouait donc à chaque rendu, et sa
   * séquence — nettoyage puis mise en place — rend le focus à l'élément d'avant
   * l'ouverture, puis le reprend sur le panneau.
   *
   * Tant que la feuille n'a porté que des formulaires dont l'état vivait dans un
   * **enfant**, rien ne s'est vu : l'appelant ne rendait pas à chaque frappe. La première
   * feuille dont le champ et le `onClose` vivent dans le même composant l'a fait sortir
   * — un seul caractère arrivait dans le champ, les suivants tombaient sur le panneau.
   *
   * Une référence plutôt qu'un `useCallback` chez chaque appelant : la correction tient
   * au composant, et aucun des six emplois n'a à connaître la règle.
   */
  const closing = useRef(onClose);
  // Tenue à jour dans un effet, et non pendant le rendu : une référence écrite pendant le
  // rendu casse le rendu concurrent, et `react-hooks/refs` le refuse à juste titre. Le
  // gestionnaire d'échappement la lit au moment de l'appui, donc bien après la validation.
  useEffect(() => {
    closing.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    restore.current = document.activeElement as HTMLElement | null;
    panel.current?.focus();

    // La page derrière ne défile plus : sans cela, un doigt qui dépasse la feuille fait
    // glisser le tableau de bord et on perd sa place en revenant.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') closing.current();
    }
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
      restore.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  /*
   * **La feuille se peint sur `body`, et c'est ce qui la met vraiment au-dessus.**
   *
   * Elle déclarait `z-index: 60` contre 30 pour la barre d'onglets, et se faisait
   * pourtant recouvrir sur ses 56 derniers pixels. Mesuré à 360 px avec
   * `elementFromPoint` : au bas de la feuille des discussions, du carnet et de la feuille
   * de séance, le doigt touchait **la barre**. Seule celle du `⊕` gagnait — parce qu'elle
   * est montée par la barre elle-même.
   *
   * Un `z-index` ne franchit pas un contexte d'empilement. Les feuilles sont rendues dans
   * `<main>`, dont le fondu d'entrée de la phase E — une `animation` sur l'opacité — en
   * crée un : leur 60 était enfermé dedans, et c'est le contexte entier qui passait sous
   * le 30 de la barre.
   *
   * Le portail règle la classe entière du problème, pour les feuilles d'aujourd'hui comme
   * pour celles qu'on ajoutera. Le remplacer par un dégagement bas dans chaque écran
   * aurait demandé de ne jamais l'oublier — et l'audit ne mesure pas le recouvrement.
   */
  return createPortal(
    <div className={styles.layer}>
      <button
        type="button"
        className={styles.scrim}
        aria-label="Fermer"
        onClick={onClose}
        tabIndex={-1}
      />

      <div
        ref={panel}
        className={cx(
          styles.panel,
          fit === true && styles.fit,
          wide === true && styles.wide,
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={
          swipe.offset > 0 ? { transform: `translateY(${String(swipe.offset)}px)` } : undefined
        }
      >
        {/* La poignée porte le geste. Elle est aussi un bouton : là où il n'y a pas de
            doigt — ou pour qui navigue au clavier — elle referme d'un appui. */}
        <button
          type="button"
          className={styles.grip}
          aria-label={`Fermer « ${title} »`}
          onClick={onClose}
          {...swipe.handlers}
        >
          <span className={styles.gripBar} />
        </button>

        <div className={cx(styles.head, action !== undefined && styles.headWithAction)}>
          <div className={styles.headText}>
            <h2 className={styles.title}>{title}</h2>
            {lede !== undefined && <p className={styles.lede}>{lede}</p>}
          </div>
          {action !== undefined && (
            <button
              type="button"
              className={styles.headAction}
              aria-label={action.label ?? action.text}
              onClick={action.onClick}
            >
              {action.icon}
              <span>{action.text}</span>
            </button>
          )}
        </div>

        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Ligne d'une feuille : une cible pleine largeur, à `--tap-lg`.
 *
 * `href` la rend `<a>`, son absence la rend `<button>`. Ce n'est pas une commodité : une
 * navigation et une action ne s'annoncent pas pareil, ne s'ouvrent pas pareil dans un
 * nouvel onglet, et ne se lisent pas pareil à la synthèse vocale.
 */
export function SheetRow({
  icon,
  label,
  hint,
  hintExplains,
  onClick,
  href,
  tone,
  children,
  'aria-label': ariaLabel,
}: {
  icon?: ReactNode | undefined;
  label?: string | undefined;
  hint?: ReactNode | undefined;
  /**
   * L'indice **explique le choix** au lieu de décrire la ligne.
   *
   * Deux natures d'indice vivaient ici sans se distinguer. « 8,4 km » est une **mesure**
   * qui appartient à la ligne : sa place est à droite, et elle se lit avec le nom.
   * « des aliments et leurs poids » est une **phrase** qui dit à quoi mène la ligne :
   * posée à droite, elle prend la moitié de la largeur et fait passer le libellé à deux
   * lignes — « Repas composé » et « Valeurs à la main » l'ont fait aux trois largeurs et
   * dans les deux thèmes, pendant que les autres entrées tenaient sur une.
   *
   * Avec ce drapeau, la phrase passe **sous** le libellé, qui garde sa largeur entière.
   * Elle quitte aussi le nom accessible — sans quoi on l'entendrait à chaque entrée d'une
   * liste qu'on parcourt.
   */
  hintExplains?: boolean | undefined;
  onClick?: (() => void) | undefined;
  href?: string | undefined;
  tone?: 'recover' | undefined;
  children?: ReactNode | undefined;
  /**
   * Nom accessible, quand l'indice ne fait pas partie de ce qu'il faut annoncer.
   *
   * Par défaut, le nom d'une ligne est son libellé **suivi de son indice** — « Course
   * 8,4 km », et c'est bien : l'indice est une donnée qui appartient à la ligne.
   * `hintExplains` couvre le cas inverse sans qu'on ait à le redire ici ; ce paramètre
   * reste pour les lignes dont le nom ne se déduit ni de l'un ni de l'autre.
   */
  'aria-label'?: string | undefined;
}) {
  const inner = children ?? (
    <>
      {icon !== undefined && <span className={styles.rowIcon}>{icon}</span>}
      <span className={styles.rowLabel}>{label}</span>
      {hint !== undefined && <span className={styles.rowHint}>{hint}</span>}
    </>
  );

  // Une phrase qui explique le choix n'entre pas dans le nom : c'est le libellé qui le
  // porte, et lui seul.
  const name = ariaLabel ?? (hintExplains === true ? label : undefined);
  const classes = cx(
    styles.row,
    hintExplains === true && styles.rowStacked,
    tone === 'recover' && styles.rowRecover,
  );

  if (href !== undefined) {
    return (
      <a className={classes} href={href} aria-label={name}>
        {inner}
      </a>
    );
  }

  return (
    <button type="button" className={classes} onClick={onClick} aria-label={name}>
      {inner}
    </button>
  );
}

/** Regroupement titré à l'intérieur d'une feuille. */
export function SheetGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.group}>
      <p className={cx('eyebrow', styles.groupTitle)}>{title}</p>
      {children}
    </section>
  );
}
